import React, { useState, useEffect, useCallback } from 'react';
import {
  BarChart3,
  FileText,
  Plus,
  Eye,
  Edit,
  Trash2,
  Calendar,
  ClipboardList,
  Users,
  Building2,
  Loader2,
  X,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { useToast } from '@/hooks/use-toast';
import reportService from '@/services/reportService';
import { cn } from '@/lib/utils';

// ============================================================
// Sabitler
// ============================================================

const REPORT_TYPE_CONFIG = {
  daily: { label: 'Günlük', className: 'bg-blue-100 text-blue-700 border-blue-200' },
  weekly: { label: 'Haftalık', className: 'bg-green-100 text-green-700 border-green-200' },
  monthly: { label: 'Aylık', className: 'bg-purple-100 text-purple-700 border-purple-200' },
  custom: { label: 'Özel', className: 'bg-orange-100 text-orange-700 border-orange-200' },
  customer_analysis: { label: 'Müşteri Analizi', className: 'bg-teal-100 text-teal-700 border-teal-200' },
  property_performance: { label: 'İlan Performansı', className: 'bg-pink-100 text-pink-700 border-pink-200' },
};

const STATUS_CONFIG = {
  draft: { label: 'Taslak', className: 'bg-gray-100 text-gray-700 border-gray-200' },
  submitted: { label: 'Gönderildi', className: 'bg-yellow-100 text-yellow-700 border-yellow-200' },
  approved: { label: 'Onaylandı', className: 'bg-green-100 text-green-700 border-green-200' },
};

const ITEMS_PER_PAGE = 10;

// ============================================================
// Yardımcı Bileşenler
// ============================================================

function ReportTypeBadge({ type }) {
  const config = REPORT_TYPE_CONFIG[type] || REPORT_TYPE_CONFIG.custom;
  return <Badge className={cn('border', config.className)}>{config.label}</Badge>;
}

function ReportStatusBadge({ status }) {
  const config = STATUS_CONFIG[status] || STATUS_CONFIG.draft;
  return <Badge className={cn('border', config.className)}>{config.label}</Badge>;
}

function formatDate(dateString) {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleDateString('tr-TR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

function formatDateTime(dateString) {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleDateString('tr-TR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function todayISO() {
  return new Date().toISOString().split('T')[0];
}

function TableSkeleton() {
  return (
    <>
      {[...Array(5)].map((_, i) => (
        <TableRow key={i}>
          <TableCell><Skeleton className="h-4 w-40" /></TableCell>
          <TableCell><Skeleton className="h-5 w-24" /></TableCell>
          <TableCell><Skeleton className="h-5 w-20" /></TableCell>
          <TableCell><Skeleton className="h-4 w-32" /></TableCell>
          <TableCell><Skeleton className="h-4 w-24" /></TableCell>
          <TableCell><Skeleton className="h-8 w-28" /></TableCell>
        </TableRow>
      ))}
    </>
  );
}

// ============================================================
// Rapor İçeriği Görüntüleme
// ============================================================

function ReportContentView({ content }) {
  if (!content || typeof content !== 'object') {
    return <p className="text-muted-foreground text-sm">İçerik bulunamadı.</p>;
  }

  return (
    <div className="space-y-4 text-sm">
      {Object.entries(content).map(([key, value]) => (
        <div key={key}>
          <h4 className="font-semibold text-foreground capitalize mb-1">
            {key.replace(/_/g, ' ')}
          </h4>
          {typeof value === 'object' && value !== null ? (
            <div className="pl-4 border-l-2 border-muted space-y-1">
              {Object.entries(value).map(([subKey, subValue]) => (
                <div key={subKey} className="flex justify-between">
                  <span className="text-muted-foreground capitalize">
                    {subKey.replace(/_/g, ' ')}
                  </span>
                  <span className="font-medium text-foreground">
                    {typeof subValue === 'object'
                      ? JSON.stringify(subValue, null, 2)
                      : String(subValue)}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-muted-foreground">{String(value)}</p>
          )}
        </div>
      ))}
    </div>
  );
}

// ============================================================
// Ana Bileşen
// ============================================================

const StaffReportsPage = () => {
  const { toast } = useToast();

  // Liste state
  const [reports, setReports] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);

  // Filtreler
  const [typeFilter, setTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateStart, setDateStart] = useState('');
  const [dateEnd, setDateEnd] = useState('');

  // Detay dialog
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);

  // Düzenleme dialog
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editReport, setEditReport] = useState(null);
  const [editLoading, setEditLoading] = useState(false);

  // Silme dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [reportToDelete, setReportToDelete] = useState(null);

  // Hızlı rapor oluşturma state
  const [quickLoading, setQuickLoading] = useState({
    daily: false,
    customer: false,
    property: false,
  });
  const [customerDateStart, setCustomerDateStart] = useState('');
  const [customerDateEnd, setCustomerDateEnd] = useState('');
  const [propertyDateStart, setPropertyDateStart] = useState('');
  const [propertyDateEnd, setPropertyDateEnd] = useState('');

  // Manuel rapor formu state
  const [manualForm, setManualForm] = useState({
    title: '',
    report_type: 'daily',
    summary: '',
    date_range_start: '',
    date_range_end: '',
    content_text: '',
    status: 'draft',
  });
  const [manualLoading, setManualLoading] = useState(false);

  // ============================================================
  // Rapor Listesi Çekme
  // ============================================================

  const fetchReports = useCallback(async () => {
    try {
      setLoading(true);
      const filters = {
        limit: ITEMS_PER_PAGE,
        offset: (currentPage - 1) * ITEMS_PER_PAGE,
        sortBy: 'created_at',
        ascending: false,
      };

      if (typeFilter !== 'all') filters.report_type = typeFilter;
      if (statusFilter !== 'all') filters.status = statusFilter;
      if (dateStart) filters.startDate = new Date(dateStart);
      if (dateEnd) filters.endDate = new Date(dateEnd);

      const { data, count, error } = await reportService.getReports(filters);

      if (error) throw error;
      setReports(data || []);
      setTotalCount(count || 0);
    } catch (error) {
      console.error('Error fetching reports:', error);
      toast({
        variant: 'destructive',
        title: 'Hata',
        description: 'Raporlar yüklenirken bir hata oluştu.',
      });
    } finally {
      setLoading(false);
    }
  }, [currentPage, typeFilter, statusFilter, dateStart, dateEnd, toast]);

  useEffect(() => {
    fetchReports();
  }, [fetchReports]);

  // Filtre değişince sayfa 1'e dön
  useEffect(() => {
    setCurrentPage(1);
  }, [typeFilter, statusFilter, dateStart, dateEnd]);

  // ============================================================
  // Detay İşlemleri
  // ============================================================

  const handleViewReport = (report) => {
    setSelectedReport(report);
    setViewDialogOpen(true);
  };

  const handleEditClick = (report) => {
    setEditReport({
      id: report.id,
      title: report.title || '',
      report_type: report.report_type || 'daily',
      summary: report.summary || '',
      date_range_start: report.date_range_start || '',
      date_range_end: report.date_range_end || '',
      content_text: typeof report.content === 'object'
        ? JSON.stringify(report.content, null, 2)
        : (report.content || ''),
      status: report.status || 'draft',
    });
    setEditDialogOpen(true);
  };

  const handleEditSave = async () => {
    if (!editReport) return;
    try {
      setEditLoading(true);

      let contentObj = {};
      try {
        contentObj = JSON.parse(editReport.content_text);
      } catch {
        contentObj = { notes: editReport.content_text };
      }

      const { error } = await reportService.updateReport(editReport.id, {
        title: editReport.title,
        report_type: editReport.report_type,
        summary: editReport.summary,
        date_range_start: editReport.date_range_start || null,
        date_range_end: editReport.date_range_end || null,
        content: contentObj,
        status: editReport.status,
      });

      if (error) throw error;

      toast({ title: 'Başarılı', description: 'Rapor güncellendi.' });
      setEditDialogOpen(false);
      setEditReport(null);
      fetchReports();
    } catch (error) {
      console.error('Error updating report:', error);
      toast({
        variant: 'destructive',
        title: 'Hata',
        description: 'Rapor güncellenirken bir hata oluştu.',
      });
    } finally {
      setEditLoading(false);
    }
  };

  const handleDeleteClick = (report) => {
    setReportToDelete(report);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!reportToDelete) return;
    try {
      const { error } = await reportService.deleteReport(reportToDelete.id);
      if (error) throw error;

      toast({ title: 'Başarılı', description: 'Rapor silindi.' });
      fetchReports();
    } catch (error) {
      console.error('Error deleting report:', error);
      toast({
        variant: 'destructive',
        title: 'Hata',
        description: 'Rapor silinirken bir hata oluştu.',
      });
    } finally {
      setDeleteDialogOpen(false);
      setReportToDelete(null);
    }
  };

  // ============================================================
  // Hızlı Rapor Oluşturma
  // ============================================================

  const handleCreateDailyReport = async () => {
    try {
      setQuickLoading((prev) => ({ ...prev, daily: true }));
      const { data, error } = await reportService.createDailyReport(new Date());
      if (error) throw error;

      toast({
        title: 'Başarılı',
        description: `Günlük rapor oluşturuldu: ${data?.title || 'Günlük Rapor'}`,
      });
      fetchReports();
    } catch (error) {
      console.error('Error creating daily report:', error);
      toast({
        variant: 'destructive',
        title: 'Hata',
        description: 'Günlük rapor oluşturulurken bir hata oluştu.',
      });
    } finally {
      setQuickLoading((prev) => ({ ...prev, daily: false }));
    }
  };

  const handleCreateCustomerAnalysis = async () => {
    if (!customerDateStart || !customerDateEnd) {
      toast({
        variant: 'destructive',
        title: 'Uyarı',
        description: 'Lütfen tarih aralığı seçin.',
      });
      return;
    }

    try {
      setQuickLoading((prev) => ({ ...prev, customer: true }));
      const { data, error } = await reportService.createCustomerAnalysisReport(
        new Date(customerDateStart),
        new Date(customerDateEnd)
      );
      if (error) throw error;

      toast({
        title: 'Başarılı',
        description: `Müşteri analizi oluşturuldu: ${data?.title || 'Müşteri Analizi'}`,
      });
      setCustomerDateStart('');
      setCustomerDateEnd('');
      fetchReports();
    } catch (error) {
      console.error('Error creating customer analysis:', error);
      toast({
        variant: 'destructive',
        title: 'Hata',
        description: 'Müşteri analizi oluşturulurken bir hata oluştu.',
      });
    } finally {
      setQuickLoading((prev) => ({ ...prev, customer: false }));
    }
  };

  const handleCreatePropertyPerformance = async () => {
    if (!propertyDateStart || !propertyDateEnd) {
      toast({
        variant: 'destructive',
        title: 'Uyarı',
        description: 'Lütfen tarih aralığı seçin.',
      });
      return;
    }

    try {
      setQuickLoading((prev) => ({ ...prev, property: true }));
      const { data, error } = await reportService.createPropertyPerformanceReport(
        new Date(propertyDateStart),
        new Date(propertyDateEnd)
      );
      if (error) throw error;

      toast({
        title: 'Başarılı',
        description: `İlan performans raporu oluşturuldu: ${data?.title || 'İlan Performansı'}`,
      });
      setPropertyDateStart('');
      setPropertyDateEnd('');
      fetchReports();
    } catch (error) {
      console.error('Error creating property performance:', error);
      toast({
        variant: 'destructive',
        title: 'Hata',
        description: 'İlan performans raporu oluşturulurken bir hata oluştu.',
      });
    } finally {
      setQuickLoading((prev) => ({ ...prev, property: false }));
    }
  };

  // ============================================================
  // Manuel Rapor Oluşturma
  // ============================================================

  const handleManualSubmit = async (e) => {
    e.preventDefault();

    if (!manualForm.title.trim()) {
      toast({
        variant: 'destructive',
        title: 'Uyarı',
        description: 'Lütfen rapor başlığı girin.',
      });
      return;
    }

    try {
      setManualLoading(true);

      let contentObj = {};
      if (manualForm.content_text.trim()) {
        try {
          contentObj = JSON.parse(manualForm.content_text);
        } catch {
          contentObj = { notes: manualForm.content_text };
        }
      }

      const { error } = await reportService.createReport({
        title: manualForm.title,
        report_type: manualForm.report_type,
        summary: manualForm.summary || null,
        date_range_start: manualForm.date_range_start || null,
        date_range_end: manualForm.date_range_end || null,
        content: contentObj,
        status: manualForm.status,
      });

      if (error) throw error;

      toast({ title: 'Başarılı', description: 'Rapor başarıyla oluşturuldu.' });
      setManualForm({
        title: '',
        report_type: 'daily',
        summary: '',
        date_range_start: '',
        date_range_end: '',
        content_text: '',
        status: 'draft',
      });
      fetchReports();
    } catch (error) {
      console.error('Error creating manual report:', error);
      toast({
        variant: 'destructive',
        title: 'Hata',
        description: 'Rapor oluşturulurken bir hata oluştu.',
      });
    } finally {
      setManualLoading(false);
    }
  };

  // ============================================================
  // Sayfalama
  // ============================================================

  const totalPages = Math.ceil(totalCount / ITEMS_PER_PAGE);

  // ============================================================
  // Render
  // ============================================================

  return (
    <div className="space-y-6">
      {/* Başlık */}
      <div>
        <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
          <BarChart3 size={24} />
          Rapor Yönetimi
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Raporları görüntüleyin, oluşturun ve yönetin
        </p>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="list" className="w-full">
        <TabsList className="grid w-full grid-cols-2 max-w-md">
          <TabsTrigger value="list">
            <ClipboardList size={16} className="mr-2" />
            Raporlarım
          </TabsTrigger>
          <TabsTrigger value="create">
            <Plus size={16} className="mr-2" />
            Rapor Oluştur
          </TabsTrigger>
        </TabsList>

        {/* ============================================ */}
        {/* TAB 1 - Raporlarım (Liste) */}
        {/* ============================================ */}
        <TabsContent value="list" className="space-y-4 mt-4">
          {/* Filtreler */}
          <Card>
            <CardContent className="pt-6">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {/* Rapor Tipi */}
                <Select value={typeFilter} onValueChange={setTypeFilter}>
                  <SelectTrigger>
                    <SelectValue placeholder="Rapor Tipi" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tümü</SelectItem>
                    <SelectItem value="daily">Günlük</SelectItem>
                    <SelectItem value="weekly">Haftalık</SelectItem>
                    <SelectItem value="monthly">Aylık</SelectItem>
                    <SelectItem value="custom">Özel</SelectItem>
                    <SelectItem value="customer_analysis">Müşteri Analizi</SelectItem>
                    <SelectItem value="property_performance">İlan Performansı</SelectItem>
                  </SelectContent>
                </Select>

                {/* Durum */}
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger>
                    <SelectValue placeholder="Durum" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tümü</SelectItem>
                    <SelectItem value="draft">Taslak</SelectItem>
                    <SelectItem value="submitted">Gönderildi</SelectItem>
                    <SelectItem value="approved">Onaylandı</SelectItem>
                  </SelectContent>
                </Select>

                {/* Tarih Başlangıç */}
                <div>
                  <Input
                    type="date"
                    value={dateStart}
                    onChange={(e) => setDateStart(e.target.value)}
                    placeholder="Başlangıç Tarihi"
                  />
                </div>

                {/* Tarih Bitiş */}
                <div>
                  <Input
                    type="date"
                    value={dateEnd}
                    onChange={(e) => setDateEnd(e.target.value)}
                    placeholder="Bitiş Tarihi"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Rapor Tablosu */}
          <Card>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Başlık</TableHead>
                      <TableHead>Rapor Tipi</TableHead>
                      <TableHead>Durum</TableHead>
                      <TableHead>Dönem</TableHead>
                      <TableHead>Oluşturulma</TableHead>
                      <TableHead className="text-right">İşlemler</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {loading ? (
                      <TableSkeleton />
                    ) : reports.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} className="text-center py-12">
                          <div className="flex flex-col items-center justify-center text-muted-foreground">
                            <FileText size={48} className="mb-3 opacity-20" />
                            <p className="text-lg font-medium mb-2">Rapor bulunamadı</p>
                            <p className="text-sm">
                              Henüz rapor oluşturulmamış veya filtrelerinize uygun rapor yok.
                            </p>
                          </div>
                        </TableCell>
                      </TableRow>
                    ) : (
                      reports.map((report) => (
                        <TableRow key={report.id}>
                          <TableCell>
                            <div className="font-medium text-foreground max-w-[250px] truncate">
                              {report.title}
                            </div>
                            {report.summary && (
                              <p className="text-xs text-muted-foreground mt-0.5 max-w-[250px] truncate">
                                {report.summary}
                              </p>
                            )}
                          </TableCell>
                          <TableCell>
                            <ReportTypeBadge type={report.report_type} />
                          </TableCell>
                          <TableCell>
                            <ReportStatusBadge status={report.status} />
                          </TableCell>
                          <TableCell>
                            <span className="text-sm text-muted-foreground">
                              {report.date_range_start && report.date_range_end
                                ? `${formatDate(report.date_range_start)} - ${formatDate(report.date_range_end)}`
                                : report.date_range_start
                                  ? formatDate(report.date_range_start)
                                  : '-'}
                            </span>
                          </TableCell>
                          <TableCell>
                            <span className="text-sm text-muted-foreground">
                              {formatDateTime(report.created_at)}
                            </span>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center justify-end gap-1">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleViewReport(report)}
                                title="Görüntüle"
                              >
                                <Eye size={14} className="mr-1" />
                                Görüntüle
                              </Button>
                              <Button
                                variant="outline"
                                size="icon"
                                className="h-8 w-8"
                                onClick={() => handleEditClick(report)}
                                title="Düzenle"
                              >
                                <Edit size={14} />
                              </Button>
                              <Button
                                variant="outline"
                                size="icon"
                                className="h-8 w-8 text-red-600 hover:text-red-700 hover:bg-red-50"
                                onClick={() => handleDeleteClick(report)}
                                title="Sil"
                              >
                                <Trash2 size={14} />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>

          {/* Sayfalama */}
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
              >
                Önceki
              </Button>
              <div className="flex items-center gap-1">
                {[...Array(totalPages)].map((_, i) => {
                  const page = i + 1;
                  if (
                    page === 1 ||
                    page === totalPages ||
                    (page >= currentPage - 1 && page <= currentPage + 1)
                  ) {
                    return (
                      <Button
                        key={page}
                        variant={currentPage === page ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => setCurrentPage(page)}
                      >
                        {page}
                      </Button>
                    );
                  } else if (page === currentPage - 2 || page === currentPage + 2) {
                    return <span key={page}>...</span>;
                  }
                  return null;
                })}
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
              >
                Sonraki
              </Button>
            </div>
          )}
        </TabsContent>

        {/* ============================================ */}
        {/* TAB 2 - Rapor Oluştur */}
        {/* ============================================ */}
        <TabsContent value="create" className="space-y-6 mt-4">
          {/* Hızlı Rapor Oluşturma Kartları */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Günlük Rapor */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Calendar size={20} className="text-blue-600" />
                  Günlük Rapor
                </CardTitle>
                <CardDescription>
                  Bugünün verilerini otomatik toplar
                </CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground mb-4">
                  Tarih: <span className="font-medium text-foreground">{todayISO()}</span>
                </p>
                <Button
                  onClick={handleCreateDailyReport}
                  disabled={quickLoading.daily}
                  className="w-full"
                >
                  {quickLoading.daily ? (
                    <Loader2 size={16} className="mr-2 animate-spin" />
                  ) : (
                    <Plus size={16} className="mr-2" />
                  )}
                  Günlük Rapor Oluştur
                </Button>
              </CardContent>
            </Card>

            {/* Müşteri Analizi */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Users size={20} className="text-teal-600" />
                  Müşteri Analizi
                </CardTitle>
                <CardDescription>
                  Müşteri verilerini analiz eder
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-xs">Başlangıç</Label>
                    <Input
                      type="date"
                      value={customerDateStart}
                      onChange={(e) => setCustomerDateStart(e.target.value)}
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Bitiş</Label>
                    <Input
                      type="date"
                      value={customerDateEnd}
                      onChange={(e) => setCustomerDateEnd(e.target.value)}
                    />
                  </div>
                </div>
                <Button
                  onClick={handleCreateCustomerAnalysis}
                  disabled={quickLoading.customer}
                  className="w-full"
                >
                  {quickLoading.customer ? (
                    <Loader2 size={16} className="mr-2 animate-spin" />
                  ) : (
                    <Plus size={16} className="mr-2" />
                  )}
                  Müşteri Analizi Oluştur
                </Button>
              </CardContent>
            </Card>

            {/* İlan Performansı */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Building2 size={20} className="text-pink-600" />
                  İlan Performansı
                </CardTitle>
                <CardDescription>
                  İlan metriklerini raporlar
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-xs">Başlangıç</Label>
                    <Input
                      type="date"
                      value={propertyDateStart}
                      onChange={(e) => setPropertyDateStart(e.target.value)}
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Bitiş</Label>
                    <Input
                      type="date"
                      value={propertyDateEnd}
                      onChange={(e) => setPropertyDateEnd(e.target.value)}
                    />
                  </div>
                </div>
                <Button
                  onClick={handleCreatePropertyPerformance}
                  disabled={quickLoading.property}
                  className="w-full"
                >
                  {quickLoading.property ? (
                    <Loader2 size={16} className="mr-2 animate-spin" />
                  ) : (
                    <Plus size={16} className="mr-2" />
                  )}
                  İlan Performansı Oluştur
                </Button>
              </CardContent>
            </Card>
          </div>

          {/* Manuel Rapor Oluşturma Formu */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <FileText size={20} />
                Manuel Rapor Oluştur
              </CardTitle>
              <CardDescription>
                Özel içerikle manuel rapor oluşturun
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleManualSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Başlık */}
                  <div className="space-y-2">
                    <Label htmlFor="manual-title">Başlık *</Label>
                    <Input
                      id="manual-title"
                      value={manualForm.title}
                      onChange={(e) =>
                        setManualForm((prev) => ({ ...prev, title: e.target.value }))
                      }
                      placeholder="Rapor başlığı"
                      required
                    />
                  </div>

                  {/* Rapor Tipi */}
                  <div className="space-y-2">
                    <Label>Rapor Tipi</Label>
                    <Select
                      value={manualForm.report_type}
                      onValueChange={(val) =>
                        setManualForm((prev) => ({ ...prev, report_type: val }))
                      }
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="daily">Günlük</SelectItem>
                        <SelectItem value="weekly">Haftalık</SelectItem>
                        <SelectItem value="monthly">Aylık</SelectItem>
                        <SelectItem value="custom">Özel</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Özet */}
                <div className="space-y-2">
                  <Label htmlFor="manual-summary">Özet</Label>
                  <Textarea
                    id="manual-summary"
                    value={manualForm.summary}
                    onChange={(e) =>
                      setManualForm((prev) => ({ ...prev, summary: e.target.value }))
                    }
                    placeholder="Rapor özeti..."
                    rows={2}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Dönem Başlangıç */}
                  <div className="space-y-2">
                    <Label htmlFor="manual-date-start">Dönem Başlangıç</Label>
                    <Input
                      id="manual-date-start"
                      type="date"
                      value={manualForm.date_range_start}
                      onChange={(e) =>
                        setManualForm((prev) => ({ ...prev, date_range_start: e.target.value }))
                      }
                    />
                  </div>

                  {/* Dönem Bitiş */}
                  <div className="space-y-2">
                    <Label htmlFor="manual-date-end">Dönem Bitiş</Label>
                    <Input
                      id="manual-date-end"
                      type="date"
                      value={manualForm.date_range_end}
                      onChange={(e) =>
                        setManualForm((prev) => ({ ...prev, date_range_end: e.target.value }))
                      }
                    />
                  </div>
                </div>

                {/* İçerik */}
                <div className="space-y-2">
                  <Label htmlFor="manual-content">İçerik / Notlar</Label>
                  <Textarea
                    id="manual-content"
                    value={manualForm.content_text}
                    onChange={(e) =>
                      setManualForm((prev) => ({ ...prev, content_text: e.target.value }))
                    }
                    placeholder="Rapor içeriği veya notlar..."
                    rows={6}
                  />
                </div>

                {/* Durum */}
                <div className="space-y-2 max-w-xs">
                  <Label>Durum</Label>
                  <Select
                    value={manualForm.status}
                    onValueChange={(val) =>
                      setManualForm((prev) => ({ ...prev, status: val }))
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="draft">Taslak</SelectItem>
                      <SelectItem value="submitted">Gönderildi</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <Button type="submit" disabled={manualLoading}>
                  {manualLoading ? (
                    <Loader2 size={16} className="mr-2 animate-spin" />
                  ) : (
                    <Plus size={16} className="mr-2" />
                  )}
                  Raporu Kaydet
                </Button>
              </form>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* ============================================ */}
      {/* Rapor Detay Dialog */}
      {/* ============================================ */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText size={20} />
              {selectedReport?.title || 'Rapor Detayı'}
            </DialogTitle>
            <DialogDescription>
              {selectedReport && (
                <span className="flex items-center gap-2 mt-1">
                  <ReportTypeBadge type={selectedReport.report_type} />
                  <ReportStatusBadge status={selectedReport.status} />
                  <span className="text-xs">
                    {formatDateTime(selectedReport.created_at)}
                  </span>
                </span>
              )}
            </DialogDescription>
          </DialogHeader>

          {selectedReport && (
            <div className="space-y-4 mt-2">
              {selectedReport.summary && (
                <div>
                  <h4 className="text-sm font-semibold text-foreground mb-1">Özet</h4>
                  <p className="text-sm text-muted-foreground">{selectedReport.summary}</p>
                </div>
              )}

              {(selectedReport.date_range_start || selectedReport.date_range_end) && (
                <div>
                  <h4 className="text-sm font-semibold text-foreground mb-1">Dönem</h4>
                  <p className="text-sm text-muted-foreground">
                    {formatDate(selectedReport.date_range_start)} - {formatDate(selectedReport.date_range_end)}
                  </p>
                </div>
              )}

              <div>
                <h4 className="text-sm font-semibold text-foreground mb-2">İçerik</h4>
                <div className="bg-muted/50 rounded-lg p-4">
                  <ReportContentView content={selectedReport.content} />
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* ============================================ */}
      {/* Rapor Düzenleme Dialog */}
      {/* ============================================ */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit size={20} />
              Raporu Düzenle
            </DialogTitle>
            <DialogDescription>Rapor bilgilerini güncelleyin</DialogDescription>
          </DialogHeader>

          {editReport && (
            <div className="space-y-4 mt-2">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Başlık</Label>
                  <Input
                    value={editReport.title}
                    onChange={(e) =>
                      setEditReport((prev) => ({ ...prev, title: e.target.value }))
                    }
                  />
                </div>
                <div className="space-y-2">
                  <Label>Rapor Tipi</Label>
                  <Select
                    value={editReport.report_type}
                    onValueChange={(val) =>
                      setEditReport((prev) => ({ ...prev, report_type: val }))
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="daily">Günlük</SelectItem>
                      <SelectItem value="weekly">Haftalık</SelectItem>
                      <SelectItem value="monthly">Aylık</SelectItem>
                      <SelectItem value="custom">Özel</SelectItem>
                      <SelectItem value="customer_analysis">Müşteri Analizi</SelectItem>
                      <SelectItem value="property_performance">İlan Performansı</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Özet</Label>
                <Textarea
                  value={editReport.summary}
                  onChange={(e) =>
                    setEditReport((prev) => ({ ...prev, summary: e.target.value }))
                  }
                  rows={2}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Dönem Başlangıç</Label>
                  <Input
                    type="date"
                    value={editReport.date_range_start}
                    onChange={(e) =>
                      setEditReport((prev) => ({
                        ...prev,
                        date_range_start: e.target.value,
                      }))
                    }
                  />
                </div>
                <div className="space-y-2">
                  <Label>Dönem Bitiş</Label>
                  <Input
                    type="date"
                    value={editReport.date_range_end}
                    onChange={(e) =>
                      setEditReport((prev) => ({
                        ...prev,
                        date_range_end: e.target.value,
                      }))
                    }
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>İçerik (JSON / Metin)</Label>
                <Textarea
                  value={editReport.content_text}
                  onChange={(e) =>
                    setEditReport((prev) => ({
                      ...prev,
                      content_text: e.target.value,
                    }))
                  }
                  rows={6}
                  className="font-mono text-sm"
                />
              </div>

              <div className="space-y-2 max-w-xs">
                <Label>Durum</Label>
                <Select
                  value={editReport.status}
                  onValueChange={(val) =>
                    setEditReport((prev) => ({ ...prev, status: val }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="draft">Taslak</SelectItem>
                    <SelectItem value="submitted">Gönderildi</SelectItem>
                    <SelectItem value="approved">Onaylandı</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <Button
                  variant="outline"
                  onClick={() => setEditDialogOpen(false)}
                >
                  İptal
                </Button>
                <Button onClick={handleEditSave} disabled={editLoading}>
                  {editLoading ? (
                    <Loader2 size={16} className="mr-2 animate-spin" />
                  ) : (
                    <Edit size={16} className="mr-2" />
                  )}
                  Kaydet
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* ============================================ */}
      {/* Silme Onay Dialog */}
      {/* ============================================ */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Raporu Sil</AlertDialogTitle>
            <AlertDialogDescription>
              Bu işlem geri alınamaz.{' '}
              <strong>{reportToDelete?.title}</strong> adlı raporu silmek
              istediğinizden emin misiniz?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>İptal</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              className="bg-red-600 hover:bg-red-700"
            >
              Sil
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default StaffReportsPage;
